import React, { useState } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { RJSFSchema, WidgetProps, ArrayFieldTemplateProps } from "@rjsf/utils";
import {
  Box,
  Checkbox,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

interface JsonSchemaAnswerRendererProps {
  schema: RJSFSchema;
  formData?: any;
  readonly?: boolean;
  showAnswerStatus?: boolean;
}

/**
 * Custom array field template for better visual presentation with collapsible accordion
 */
const CustomArrayFieldTemplate: React.FC<ArrayFieldTemplateProps> = (props) => {
  const { items, title } = props;
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: "48px",
            "&.Mui-expanded": { minHeight: "48px" },
            bgcolor: "grey.50",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {title && (
              <Typography variant="subtitle2" fontWeight="medium">
                {title}
              </Typography>
            )}
            <Chip
              label={`${items.length} item${items.length !== 1 ? "s" : ""}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {items.map((item, index) => (
              <Card
                key={item.key}
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="primary"
                      sx={{
                        bgcolor: "primary.50",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      Item {index + 1}
                    </Typography>
                  </Box>
                  {item.children}
                </CardContent>
              </Card>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

/**
 * Custom checkbox widget that displays a green checkmark for checked boxes
 * and a red X for unchecked boxes when showAnswerStatus is true.
 */
const CustomCheckboxWidget: React.FC<
  WidgetProps & { showAnswerStatus?: boolean }
> = (props) => {
  const { value, label, readonly, showAnswerStatus } = props;

  if (readonly && showAnswerStatus) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Checkbox
          checked={value || false}
          readOnly
          icon={<CloseIcon sx={{ color: "error.main" }} />}
          checkedIcon={<CheckBoxIcon sx={{ color: "success.main" }} />}
          sx={{
            padding: "4px",
            "&.Mui-checked": {
              color: "success.main",
            },
            "&:not(.Mui-checked)": {
              color: "error.main",
            },
          }}
        />
        <Box component="span" sx={{ fontSize: "0.875rem" }}>
          {label}
        </Box>
      </Box>
    );
  }

  if (readonly && !showAnswerStatus) {
    // For readonly preview mode, show regular checkbox with label
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Checkbox
          checked={value || false}
          readOnly
          sx={{
            padding: "4px",
          }}
        />
        <Box component="span" sx={{ fontSize: "0.875rem" }}>
          {label}
        </Box>
      </Box>
    );
  }

  // For non-readonly mode, use default behavior
  return (
    <Checkbox
      checked={value || false}
      onChange={(event) => props.onChange(event.target.checked)}
    />
  );
};

/**
 * Renders a JSON answer using react-jsonschema-form.
 * Displays structured data according to its JSON schema in a user-friendly format.
 * Can be used in read-only or editable mode.
 */
const JsonSchemaAnswerRenderer: React.FC<JsonSchemaAnswerRendererProps> = ({
  schema,
  formData,
  readonly = true,
  showAnswerStatus = false,
}) => {
  return (
    <Box
      sx={{
        ...(readonly && {
          "& .MuiFormControl-root": {
            pointerEvents: "none",
            mb: 1.5,
          },
          "& .MuiInputBase-input": {
            cursor: "default",
          },
          "& .MuiButton-root": {
            display: "none",
          },
          "& fieldset": {
            borderColor: "rgba(0, 0, 0, 0.12) !important",
          },
          "& .MuiFormLabel-root": {
            fontSize: "0.875rem",
            fontWeight: 500,
          },
          "& .MuiCheckbox-root": {
            padding: "4px",
          },
          "& .MuiFormControlLabel-root": {
            mb: 0.5,
          },
          // Allow accordion interaction even in readonly mode
          "& .MuiAccordion-root": {
            pointerEvents: "auto",
          },
        }),
      }}
    >
      <Form
        schema={schema}
        formData={formData}
        validator={validator}
        readonly={readonly}
        disabled={false}
        uiSchema={{
          "ui:submitButtonOptions": {
            norender: true,
          },
        }}
        widgets={{
          CheckboxWidget: (props: WidgetProps) => (
            <CustomCheckboxWidget
              {...props}
              showAnswerStatus={showAnswerStatus}
            />
          ),
        }}
        templates={{
          ArrayFieldTemplate: CustomArrayFieldTemplate,
        }}
      >
        <div />
      </Form>
    </Box>
  );
};

export default JsonSchemaAnswerRenderer;
